import {
  defineComponent,
  computed,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLDFlag } from 'launchdarkly-vue-client-sdk';
import { FeatureFlags } from '@/plugins/launchDarkly/featureFlags';
import { useNavigation } from '@/composables/useNavigation';
import store from "@/store";
import { getVaultNovaEnvironmentPath } from "@/utils/platform.config";

export default defineComponent({
  name: "Navigation",
  emits: ["openDrawer"],
  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();
    const { navigationArray } = useNavigation();

    const featureFlagChecks: Record<string, { value: boolean }> = {
      [FeatureFlags.PAYMENTS_INTEGRATION]: useLDFlag(FeatureFlags.PAYMENTS_INTEGRATION, false),
      [FeatureFlags.NOTIFICATION_SETTINGS]: useLDFlag(FeatureFlags.NOTIFICATION_SETTINGS, false),
      [FeatureFlags.IMPORT_HUB]: useLDFlag(FeatureFlags.IMPORT_HUB, false),
    };

    function firstTwoSegmentsMatch(pathA: any, pathB: any) {
      const getFirstTwo = (path: string) =>
        path?.split("/").filter(Boolean).slice(0, 2).join("/");
      return getFirstTwo(pathA) === getFirstTwo(pathB);
    }

    // Map navigation structure from composable to match existing template expectations
    // Make this a computed property so it reacts to store.state.userVaultnPermissions changes
    const list = computed(() => 
      navigationArray.value.map((item: any, index: number) => {
        // Check if this is a standalone item (has path) or a parent item (has children)
        const isStandalone = item.path && item.children.length === 0;

        return {
          id: index + 1,
          node: item.name,
          path: isStandalone ? item.path : (item.children.length === 1 ? item.children[0].path : ''),
          iconImage: item.icon || '', // Use iconImage for SVG icons instead of iconClass
          isActive: isStandalone
            ? firstTwoSegmentsMatch(route.path, item.path)
            : item.children.some((child: any) => firstTwoSegmentsMatch(route.path, child.path)),
          // Add parent item permissions
          rbacRole: item.rbacRole || [],
          ownerRole: item.ownerRole || [],
          subnodes: isStandalone ? [] : item.children.map((child: any, childIndex: number) => ({
            id: childIndex + 1,
            node: child.label,
            path: child.path,
            icon: child.icon,
            featureFlag: child.featureFlag,
            // Use actual permission data from routes
            rbacRole: child.rbacRole || [],
            ownerRole: child.ownerRole || [],
            actionPermissions: child.actionPermissions || [],
            viewPermissions: child.viewPermissions || []
          }))
        };
      })
    );

    function hasPermissions(rbacRoles: string[], ownerRoles: string[]): boolean {
      // If has rbacRoles, check if user has at least one
      if (rbacRoles.length > 0) {
        const hasRbacPermission = rbacRoles.some((role: string) =>
          store.state.userVaultnPermissions.includes(role)
        );
        if (!hasRbacPermission) {
          return false;
        }
      }
      
      // If has ownerRoles, check if user's role matches
      if (ownerRoles.length > 0) {
        const hasOwnerRole = ownerRoles.includes(store.state.ownerRole);
        if (!hasOwnerRole) {
          return false;
        }
      }
      
      return true;
    }

    function isItemVisible(item: any): boolean {
      // Check feature flags
      const itemFeatureFlag = (item as any).featureFlag;
      if (itemFeatureFlag && featureFlagChecks[itemFeatureFlag]) {
        if (!featureFlagChecks[itemFeatureFlag].value) {
          return false;
        }
      }
      
      // Check permissions
      const rbacRoles = item.rbacRole || [];
      const ownerRoles = item.ownerRole || [];
      
      return hasPermissions(rbacRoles, ownerRoles);
    }

    const filteredList = computed(() =>
      list.value.filter((item: any) => {
        // Check parent item visibility
        if (!isItemVisible(item)) {
          return false;
        }
        
        // For parent items with subnodes, check if at least one subnode is visible
        if ((item.subnodes ?? []).length > 0) {
          const hasVisibleSubnodes = item.subnodes.some((subnode: any) => isItemVisible(subnode));
          if (!hasVisibleSubnodes) {
            return false;
          }
        }
        
        return true;
      })
    );

    function handleHeaderClick(item: any) {
      if (!item.isActive) {
        item.isActive = true;
        emit("openDrawer");
      }

      // If no subnodes, navigation is handled by router-link
      if ((item.subnodes ?? []).length === 0) {
        if (item.path) {
          router.push(item.path);
        }
        return;
      }

      // For items with subnodes, navigate to first visible subnode
      const visibleSubnodes = item.subnodes.filter((subnode: any) => isSubnodeVisible(subnode));
      if (visibleSubnodes.length > 0) {
        const firstSubnode = visibleSubnodes[0];
        if (firstSubnode.path) {
          // Check if this is an external route
          if (isExternalRouteSubnode(firstSubnode, item.node)) {
            const baseUrl = getVaultNovaEnvironmentPath();
            const fullUrl = `${baseUrl}${firstSubnode.path}`;
            window.location.href = fullUrl;
          } else {
            router.push(firstSubnode.path);
          }
        }
      }
    }

    function getIconSource(iconImage: string): string {
      if (!iconImage || iconImage.length === 0) {
        return require('@/assets/images/icons/dashboard-icon.svg');
      }
      const filename = iconImage.split('/').pop();
      return require(`@/assets/images/icons/${filename}`);
    }

    function isPaymentsSubnode(parentNode: string): boolean {
      return parentNode === 'Payments & Billing';
    }

    function isImportHubSubnode(parentNode: string): boolean {
      return parentNode === 'Import Hub';
    }

    function isPromotionsSubnode(subnode: any, parentNode: string): boolean {
      return parentNode === 'Distribution' && subnode.node === 'Promotions';
    }

    function isExternalRouteSubnode(subnode: any, parentNode: string): boolean {
      return isPaymentsSubnode(parentNode)
        || isPromotionsSubnode(subnode, parentNode)
        || isImportHubSubnode(parentNode)
        || subnode.node === 'Notification Settings'
        || subnode.node === 'Wallet Provider'
        || subnode.node === 'Developer';
    }

    function handleSubnodeClick(subnode: any, parentNode: string, event: Event) {
      if (isExternalRouteSubnode(subnode, parentNode)) {
        event.preventDefault();
        const baseUrl = getVaultNovaEnvironmentPath();
        const fullUrl = `${baseUrl}${subnode.path}`;
        window.location.href = fullUrl;
      }
    }

    function isSubnodeVisible(subnode: any): boolean {
      return isItemVisible(subnode);
    }

    function handleStandaloneClick(item: any) {
      // For standalone items, just navigate to the path
      if (item.path) {
        router.push(item.path);
      }
    }

    // No need for watch since list is now computed and automatically reacts to route.path changes

    return {
      list,
      store,
      route,
      router,
      firstTwoSegmentsMatch,
      handleHeaderClick,
      handleStandaloneClick,
      filteredList,
      getIconSource,
      isPaymentsSubnode,
      isExternalRouteSubnode,
      handleSubnodeClick,
      isSubnodeVisible,
    };
  },
});
